import React, { useState, useEffect } from 'react'
import "../css/Quotation.css"
import { Link, useLocation } from 'react-router-dom';
import { domain } from './Domain';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { motion } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';

/** Stable fallbacks so useEffect deps do not change identity every render. */
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const Quotation = () => {
    const location = useLocation();
    const navState = location.state;
    const products = navState?.products ?? EMPTY_ARRAY;
    const cartItems = navState?.cartItems ?? EMPTY_ARRAY;
    const info = navState?.info ?? EMPTY_OBJECT;

    const canShowQuotation =
        Array.isArray(cartItems) &&
        cartItems.length > 0 &&
        Array.isArray(products) &&
        products.length > 0;
    const [total, setTotal] = useState(0)
    const [quotationNumber, setQuotationNumber] = useState('');
    const [base64Images, setBase64Images] = useState({});

    const getProduct = (productId) => {
        const response = products && products.length > 0 && products.find(product => product.id === parseInt(productId))
        return response
    }

    const getTotal = () => {
        let total = 0;
        if (products?.length && cartItems?.length) {
            cartItems.forEach((item) => {
                const foundItem = products.find(
                    (dataItem) => dataItem.id === item.product
                );
                if (foundItem) {
                    total += foundItem.selling_price * item.quantity;
                }
            });
        }
        return total;
    };

    function addOneMonth(date) {
        // Copy the date to avoid mutating the original
        const newDate = new Date(date);

        // Get current month and year
        let currentMonth = newDate.getMonth();
        let currentYear = newDate.getFullYear();

        // Calculate next month
        currentMonth += 1;

        // Check if we need to increment the year
        if (currentMonth > 11) {
            currentMonth = 0; // January
            currentYear += 1;
        }

        // Set the new month and year
        newDate.setMonth(currentMonth);
        newDate.setFullYear(currentYear);

        return newDate;
    }
    
    const downloadPDFs = () => {
        const input = document.getElementById('pdf-content');

        html2canvas(input, { scale: 2 })
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210; // A4 width in mm (8.27in)
                const imgHeight = canvas.height * imgWidth / canvas.width; // Maintain aspect ratio
                pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
                pdf.save(`quotation - ${quotationNumber}.pdf`);
            });
    };

    const downloadPDF = () => {
        const input = document.getElementById('pdf-content');
    
        html2canvas(input, { scale: 2 })
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210; // A4 width in mm
                const imgHeight = canvas.height * imgWidth / canvas.width; // Maintain aspect ratio
                const pageHeight = 297; // A4 height in mm
                let heightLeft = imgHeight;
                let position = 0;
    
                // Add the first page
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
    
                // Add additional pages
                while (heightLeft > 0) {
                    position -= pageHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }
    
                pdf.save(`quotation-${quotationNumber}.pdf`);
            });
    };
    

    const fetchAndConvertToBase64 = (imageUrl) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.onload = function () {
                const reader = new FileReader();
                reader.onloadend = function () {
                    resolve(reader.result.split(',')[1]); // Extract base64 string from data URL
                };
                reader.readAsDataURL(xhr.response);
            };
            xhr.onerror = function () {
                reject(new Error('Failed to fetch image'));
            };
            xhr.open('GET', imageUrl);
            xhr.responseType = 'blob';
            xhr.send();
        });
    };

    useEffect(() => {
        if (!canShowQuotation) {
            return;
        }
        const fetchImages = async () => {
            const base64Map = {};
            for (const item of cartItems) {
                const imageUrl = domain + getProduct(item.product)?.image;
                if (!imageUrl || imageUrl === domain) continue;
                try {
                    const base64 = await fetchAndConvertToBase64(imageUrl);
                    base64Map[item.id] = base64;
                } catch (error) {
                    console.error('Error converting image to Base64:', error);
                }
            }
            setBase64Images(base64Map);
        };

        fetchImages();
    }, [canShowQuotation, cartItems, products]);

    const generateQuotationNumber = () => {
        const randomNumber = Math.floor(Math.random() * 10000); // Generate random number between 0 and 9999
        const formattedNumber = randomNumber.toString().padStart(4, '0'); // Pad with leading zeros if necessary
        setQuotationNumber(`${new Date().getFullYear()}-IPE-${formattedNumber}`);
    };

    useEffect(() => {
        if (!canShowQuotation) {
            return;
        }
        setTotal(getTotal());
        generateQuotationNumber();
    }, [canShowQuotation, cartItems, products]);

    if (!canShowQuotation) {
        return (
            <div className="quotation-bg">
                <Header />
                <div className="quotation-body">
                    <div className="container flex-grow-1 d-flex align-items-center py-4 py-md-5">
                        <div className="quotation-empty-wrap">
                            <motion.div
                                className="quotation-empty-card w-100"
                                initial={{ opacity: 0, y: 28 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.5,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            >
                                <div className="quotation-empty-inner">
                                    <header className="quotation-empty-header">
                                        <div
                                            className="quotation-empty-icon"
                                            aria-hidden
                                        >
                                            <i className="fa-solid fa-file-invoice" />
                                        </div>
                                        <div className="quotation-empty-header-text">
                                            <span className="quotation-empty-badge">
                                                Pro-forma invoice
                                            </span>
                                            <p className="quotation-empty-eyebrow">
                                                Nothing to show yet
                                            </p>
                                            <h1 className="quotation-empty-title">
                                                Create a quotation from your cart
                                            </h1>
                                            <p className="quotation-empty-lead">
                                                Your PDF opens here after you generate it
                                                from the cart with line items and company
                                                details. This page has no saved session, 
                                                bookmarks or refresh won&apos;t restore a
                                                quotation.
                                            </p>
                                        </div>
                                    </header>

                                    <section
                                        className="quotation-empty-section"
                                        aria-labelledby="quotation-how-heading"
                                    >
                                        <h2
                                            id="quotation-how-heading"
                                            className="quotation-empty-section-title"
                                        >
                                            <i
                                                className="fa-solid fa-list-check me-2"
                                                aria-hidden
                                            />
                                            How to generate one
                                        </h2>
                                        <ol className="quotation-empty-timeline">
                                            <li className="quotation-empty-timeline-item">
                                                <span className="quotation-empty-dot">
                                                    1
                                                </span>
                                                <div>
                                                    <strong className="quotation-empty-step-title">
                                                        Shop the catalog
                                                    </strong>
                                                    <p className="quotation-empty-step-desc">
                                                        Add the industrial parts you need to
                                                        your cart.
                                                    </p>
                                                </div>
                                            </li>
                                            <li className="quotation-empty-timeline-item">
                                                <span className="quotation-empty-dot">
                                                    2
                                                </span>
                                                <div>
                                                    <strong className="quotation-empty-step-title">
                                                        Company &amp; tax details
                                                    </strong>
                                                    <p className="quotation-empty-step-desc">
                                                        In the cart, open the quotation
                                                        dialog and fill in all required
                                                        fields.
                                                    </p>
                                                </div>
                                            </li>
                                            <li className="quotation-empty-timeline-item">
                                                <span className="quotation-empty-dot">
                                                    3
                                                </span>
                                                <div>
                                                    <strong className="quotation-empty-step-title">
                                                        Generate &amp; download
                                                    </strong>
                                                    <p className="quotation-empty-step-desc">
                                                        Submit to land here with your
                                                        pro-forma and PDF download.
                                                    </p>
                                                </div>
                                            </li>
                                        </ol>
                                    </section>

                                    <footer className="quotation-empty-footer">
                                        <div className="quotation-empty-actions">
                                            <Link
                                                to="/cart"
                                                className="quotation-empty-btn quotation-empty-btn--primary"
                                            >
                                                <i
                                                    className="fa-solid fa-cart-shopping"
                                                    aria-hidden
                                                />
                                                Go to cart
                                            </Link>
                                            <Link
                                                to="/"
                                                className="quotation-empty-btn quotation-empty-btn--ghost"
                                            >
                                                <i
                                                    className="fa-solid fa-arrow-left-long"
                                                    aria-hidden
                                                />
                                                Back to shop
                                            </Link>
                                        </div>
                                        <p className="quotation-empty-hint">
                                            <i
                                                className="fa-solid fa-lightbulb"
                                                aria-hidden
                                            />
                                            Tip: keep your cart tab open until you&apos;ve
                                            downloaded the quotation you need.
                                        </p>
                                    </footer>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="quotation-bg">
            <Header />
            <div className="quotation-body">
            <div className='QuotationHeader' id='pdf-content'>
                <div className='one'>
                    <img src='/images/logo.jpg' />
                    <div>
                        <p>Industrial Parts & Engineering Services Ltd</p>
                        <p>Pioneer House Plot 28, Kampala Floor Rm9 </p>
                        <p>P.O Box 122698 Kampala Uganda </p>
                        <p>Phone: 0392174765/0706314991/0758499705</p>
                        <p> Email: <a>industrialpartsandengineering@gmail.com</a></p>
                    </div>
                </div>
                <div className='two'>
                    <div>
                        <p>{info && info.company}</p>
                        <p>{info && info.location}</p>
                        <p>{info && info.city}</p>
                        <p>Uganda</p>
                        <p>Tax ID: {info && info.tin}</p>
                    </div>
                </div>
                <h2>Pro-Forma Invoice # {quotationNumber}</h2>
                <div className='three'>
                    <div>
                        <h3>Quotation Date:</h3>
                        <p>{new Date().toLocaleDateString()}</p>
                    </div>
                    <div>
                        <h3>Expiration:</h3>
                        <p>{addOneMonth(new Date()).toLocaleDateString()}</p>
                    </div>
                    <div>
                        <h3>Salesperson:</h3>
                        <p>System Generated</p>
                    </div>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Details</th>
                            <th>Image</th>
                            <th>Quantity</th>
                            <th>Unit Price (Ugx)</th>
                            <th>Amount (Ugx)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cartItems.map(item => (
                            <tr key={item.id}>
                                <td>{getProduct(item.product)?.product_name}</td>
                                <td>
                                    code: {getProduct(item.product)?.item_code} <br />
                                    part No: {getProduct(item.product)?.part_number} <br />
                                    condition: {getProduct(item.product)?.condition}
                                </td>
                                <td className='text-center'>
                                    {base64Images[item.id] && (
                                        <img src={`data:image/jpeg;base64,${base64Images[item.id]}`} width={50} alt="Product" />
                                    )}
                                </td>

                                <td>{item.quantity} PIECES</td>
                                <td>{getProduct(item.product)?.selling_price.toLocaleString()}</td>
                                <td>{(getProduct(item.product)?.selling_price * item.quantity).toLocaleString()}</td>
                            </tr>
                        ))}

                    </tbody>
                </table>
                <div className='four'>
                    <div className='parent'>
                        <div>
                            <p>Untaxed Amount</p>
                            <p>{total.toLocaleString()}</p>
                        </div>
                        <div>
                            <p>Sales Tax 18%</p>
                            <p>{(total * 0.18).toLocaleString()}</p>
                        </div>
                        <div>
                            <p>Total</p>
                            <p>Ugx {(total * 1.18).toLocaleString()}</p>
                        </div>
                    </div>
                </div>
                <div className='five'>
                    <p className='p'>ORDER BY LPO</p>
                    <div>
                        <p>DELIVERY PERIOD: IMMEDIATELY AFTER LPO</p>
                        <p><b>PAYMENT TYPE:</b> <span>EFT/CASH/CHEQUE</span></p>
                    </div>
                    <div>
                        <p><b>BANK:</b> CENTENARY BANK,  BRANCH-MAPEERA</p>
                        <p><b>ACCOUNT NAME:</b>  INDUSTRIAL PARTS AND ENGINEERING SERVICES LTD</p>
                        <p><b>ACCOUNT NO</b> 3100022148</p>
                        <p><b>TIN NUMBER:</b> 1007756677</p>
                    </div>
                </div>
            </div>
            <div className='QuotationHeader pt-0'>
                <button className='btn btn-danger' onClick={downloadPDF}>Download Quotation</button>
            </div>
            </div>
            <Footer />
        </div>
    )
}

export default Quotation